import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, BookOpen, Trophy, Clock, CheckCircle, Lock, Sparkles } from 'lucide-react'
import lessonsData from '../data/lessons.json'
import Footer from '../components/Footer'
import '../styles/module.css'

/**
 * ModuleScreen Component
 * Displays lessons within a specific module
 */
const ModuleScreen = () => {
  const { moduleId } = useParams()
  const navigate = useNavigate()
  const [_module, setModule] = useState(null)
  const [lessons, setLessons] = useState([])
  const [loading, setLoading] = useState(true)

  // Module information (should come from API)
  const moduleInfo = {
    1: { title: 'Before the Blast', icon: '🛡️', color: '#3b82f6' },
    2: { title: 'Fallout 101', icon: '☢️', color: '#f59e0b' },
    3: { title: 'Safe Shelter Basics', icon: '🏠', color: '#10b981' },
    4: { title: 'Water, Food & First Aid', icon: '💧', color: '#06b6d4' }
  }

  useEffect(() => {
    // Fetch module data
    const fetchModuleData = async () => {
      try {
        // Get module info from API
        const response = await fetch(`/api/modules/${moduleId}`)
        const moduleData = await response.json()
        setModule(moduleData)

        // Get lessons from local data (will be API later)
        const moduleLessons = lessonsData[moduleId] || []
        setLessons(moduleLessons)
      } catch (error) {
        console.error('Error fetching module data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchModuleData()
  }, [moduleId])

  const currentModule = moduleInfo[moduleId] || { title: 'Unknown Module', icon: '❓', color: '#6b7280' }

  // Mock progress data (will come from API)
  const getProgress = (lessonId) => {
    // Simulate some lessons being completed
    const completed = ['1-1', '2-1'].includes(lessonId)
    return { completed, score: completed ? 100 : 0 }
  }

  const completedCount = lessons.filter(l => getProgress(l.id).completed).length
  const progressPercent = lessons.length > 0 ? Math.round((completedCount / lessons.length) * 100) : 0

  return (
    <div className="module-shell">
      <div className="landing-ambient" />

      {loading ? (
        <div className="container">
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading lessons...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Header */}
          <header className="module-header">
            <div className="container">
              <motion.button
                className="back-button"
                onClick={() => navigate('/')}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <ArrowLeft size={20} />
                <span>Back to Home</span>
              </motion.button>

              <motion.div
                className="module-info"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="module-icon-large" style={{ background: `${currentModule.color}20`, borderColor: currentModule.color }}>
                  {currentModule.icon}
                </div>
                <div>
                  <h1 className="module-title">{currentModule.title}</h1>
                  <p className="module-progress">
                    {completedCount} of {lessons.length} lessons completed
                  </p>
                  <div className="module-progress-bar">
                    <div
                      className="module-progress-fill"
                      style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${currentModule.color}, #f97316)` }}
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </header>

          {/* Lessons List */}
          <main className="container module-main">
            <section className="lessons-section">
              <motion.div
                className="section-header"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <span className="eyebrow">Your Learning Path</span>
                <h2 className="section-title">Lessons</h2>
                <p className="section-subtitle">Complete each lesson to unlock the next and earn XP rewards.</p>
              </motion.div>

              <div className="lessons-list">
                {lessons.map((lesson, index) => {
                  const progress = getProgress(lesson.id)
                  const isLocked = index > 0 && !getProgress(lessons[index - 1].id).completed

                  return (
                    <motion.div
                      key={lesson.id}
                      className={`lesson-card ${progress.completed ? 'completed' : ''} ${isLocked ? 'locked' : ''}`}
                      onClick={() => !isLocked && navigate(`/lesson/${lesson.id}`)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + index * 0.05 }}
                      whileHover={!isLocked ? { scale: 1.01, y: -2 } : {}}
                    >
                      <div className="lesson-number">
                        {progress.completed ? (
                          <CheckCircle size={24} />
                        ) : isLocked ? (
                          <Lock size={24} />
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>

                      <div className="lesson-content">
                        <h3 className="lesson-title">{lesson.title}</h3>
                        <div className="lesson-meta">
                          <span className="xp-reward">
                            <Trophy size={16} />
                            {lesson.xpReward} XP
                          </span>
                          <span className="time-estimate">
                            <Clock size={16} />
                            ~5 min
                          </span>
                        </div>
                      </div>

                      {progress.completed && (
                        <div className="lesson-score">
                          <span className="score-label">Score</span>
                          <span className="score-value">{progress.score}%</span>
                        </div>
                      )}
                    </motion.div>
                  )
                })}
              </div>

              {/* Module completion bonus */}
              <motion.div
                className="completion-bonus"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="bonus-icon">
                  <Sparkles size={28} />
                </div>
                <div className="bonus-content">
                  <h3>Module Completion Bonus</h3>
                  <p>Complete all lessons to earn <strong>+200 XP</strong> and unlock the <strong>"{currentModule.title} Master"</strong> badge!</p>
                </div>
                <div className="bonus-progress">
                  <span className="bonus-count">{completedCount}/{lessons.length}</span>
                </div>
              </motion.div>
            </section>
          </main>

          <Footer />
        </>
      )}
    </div>
  )
}

export default ModuleScreen
